import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'

import type { AnalyticsEventData } from '../api/types'
import { UNEXPECTED_ERROR_MESSAGE } from '../constants'
import { redirectTo } from '../utils/redirect'

export interface UseAuthBaseOptions {
  appId?: string
  featureCode?: string
  fromComponent?: string
  nextUrl?: string
  scope?: string
  eventData?: AnalyticsEventData
  initialEmail?: string
}

export interface UseAuthBaseReturn {
  isLoading: boolean
  error: string | undefined
  setError: (error: string | undefined) => void
  setIsLoading: (loading: boolean) => void
  buildEventData: (type: string) => AnalyticsEventData
  navigateToCallback: (route: string, queryParams?: Record<string, string>) => void
  redirectToCallback: (url: string) => void
  handleAsyncOperation: <T>(operation: () => Promise<T>) => Promise<T | null>
}

/**
 * Base hook that provides common authentication functionality
 */
export function useAuthBase(options: UseAuthBaseOptions = {}): UseAuthBaseReturn {
  const { appId, featureCode, fromComponent, eventData } = options
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const buildEventData = useCallback(
    (type: string): AnalyticsEventData => {
      return {
        app_id: appId || 'bifrost',
        feature_code: featureCode,
        from_component: fromComponent,
        type,
        ...eventData,
      }
    },
    [appId, featureCode, fromComponent, eventData]
  )

  const navigateToCallback = useCallback(
    (pathname: string, queryParams?: Record<string, string>) => {
      router.push({
        pathname,
        query: { ...router.query, ...queryParams },
      })
    },
    [router]
  )

  const redirectToCallback = useCallback((url: string) => {
    redirectTo(url)
  }, [])

  const handleAsyncOperation = useCallback(
    async <T>(operation: () => Promise<T>): Promise<T | null> => {
      setIsLoading(true)
      setError(undefined)

      try {
        return await operation()
      } catch (err) {
        setError(UNEXPECTED_ERROR_MESSAGE)
        return null
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  return {
    isLoading,
    error,
    setError,
    setIsLoading,
    buildEventData,
    navigateToCallback,
    redirectToCallback,
    handleAsyncOperation,
  }
}
